import React, { useEffect, useState } from "react";
import {
    useGetDiscountByIdQuery,
    useUpdateDiscountMutation,
} from "../Service/discountsApi";
import { useParams } from 'react-router-dom';
import { discountValue } from '../initialValues';
import { DiscountsRoutes } from '../../../Router/path';
import { FormRenderGroupTypes } from '../../Shared/Components/FormRenderer/FormRenderGroup.types';
import SettingsIcon from '@mui/icons-material/Settings';
import InfoIcon from '@mui/icons-material/Info';
import { CircularProgress, Typography } from '@mui/material';
import EntityFormLayout from '../../Shared/Components/EntityFormLayout/EntityFormLayout';
import { discountFields } from '../fields';
import {
  useAssignBookingIdsToDiscountMutation,
  useGetDiscountWithBookingIdsQuery,
} from '../Service/extendedDiscountApi';
import { useGetBookingListQuery } from '../../Bookings/Service/extendedBookingsApi';
import BookOnlineIcon from '@mui/icons-material/BookOnline';
import { MultiAssign } from '../../Shared/Components/MultiAssign';
import AccordionCard from '../../Shared/Components/AccordionCard/AccordionCard';
import { useEntityForm } from '../../Shared/Hooks/useEntityForm';

const EditDiscount = () => {
    const discountId = parseInt(useParams<{ discountId: string }>().discountId!);
    const {
        data: discountData,
        isLoading: isDiscountLoading,
        error: discountError,
    } = useGetDiscountByIdQuery(discountId, { skip: !discountId });

    const [updateDiscount, updateStatus] = useUpdateDiscountMutation();

    const {
        data: discountWithBookings,
        isLoading: discountWithBookingsLoading,
        isError: discountWithBookingsError,
    } = useGetDiscountWithBookingIdsQuery(discountId, { skip: !discountId });

    const { data: bookingsPage, isLoading: loadingBookings } =
        useGetBookingListQuery({ pageNumber: 1, pageSize: 1000, includeCancelled: true });

    const entityForm = useEntityForm({
        initialData: discountData ?? discountValue,
        fields: discountFields,
        submitFn: (data) => {
            const payload = {
                id: discountId,
                data: data,
            };
            return updateDiscount(payload).unwrap();
        },
        redirectUrl: DiscountsRoutes.root,
        mutationStatus: updateStatus,
        groupOrder: [
            FormRenderGroupTypes.basic,
            FormRenderGroupTypes.configuration,
        ],
        groupIcons: {
            Podstawowe: (
                <InfoIcon sx={{ color: (theme) => theme.palette.primary.main }} />
            ),
            Konfiguracja: (
                <SettingsIcon sx={{ color: (theme) => theme.palette.primary.main }} />
            ),
        },
    });
    const [selectedBookingIds, setSelectedBookingIds] = useState<number[]>([]);

    useEffect(() => {
        if (discountWithBookings) {
            setSelectedBookingIds(discountWithBookings.bookingIds);
        }
    }, [discountWithBookings]);


    const [assignBookings, { isLoading: isAssigning }] =
        useAssignBookingIdsToDiscountMutation();

    if (isDiscountLoading || discountWithBookingsLoading) {
        return (
            <Typography align="center" sx={{ mt: 4 }}>
                <CircularProgress />
            </Typography>
        );
    }

    if (discountError || discountWithBookingsError) {
        return (
            <Typography color="error" align="center" sx={{ mt: 4 }}>
                Nie udało się załadować danych zniżki.
            </Typography>
        );
    }
    // Cancelled bookings stay selectable only when already assigned, so nothing is silently dropped.
    const assignedBookingIds = discountWithBookings?.bookingIds ?? [];
    const bookingOptions = (bookingsPage?.items ?? []).filter(
        (b) => !b.cancelledAtUtc || assignedBookingIds.includes(b.id)
    );

    return (
        <EntityFormLayout {...entityForm}>
            <AccordionCard title="Przypisz rezerwacje do zniżki" icon={<BookOnlineIcon fontSize="large" color="primary" />}>
                <MultiAssign

                    options={bookingOptions}
                    loadingOptions={loadingBookings || discountWithBookingsLoading}
                    selectedIds={selectedBookingIds}
                    onChange={setSelectedBookingIds}
                    getOptionLabel={(b) =>
                        `${b.bookingDateFrom.slice(0, 10)} • ${b.offerName}${b.cancelledAtUtc ? ' (anulowany)' : ''}`
                    }
                    getOptionValue={(b) => b.id}
                    onSave={async () => {
                        await assignBookings({ discountId: discountId, bookingIds: selectedBookingIds }).unwrap();

                    }}
                    saving={isAssigning}
                    label="Wybierz rezerwacje"
                    placeholder="Zacznij pisać…"
                />
            </AccordionCard>
        </EntityFormLayout>
    );
};

export default EditDiscount;
